import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type Props = {
    /** The decision being made — the only headline on the card. */
    name: string;
    /** Facts that help choose, e.g. ['12–17 anos']. Rendered before the section count. */
    meta?: (string | undefined)[];
    /** Section titles behind the count. Omit for options that have no sections. */
    sections?: string[];
    /** Top thumbnail. A src renders the image; `null` reserves an empty slot
        (keeps rows aligned while art is pending); omit for text-only cards. */
    image?: string | null;
    onSelect: () => void;
};

/**
 * A template choice: the name, a line of facts, and nothing else. The section
 * count opens the full list on click, so the detail is available without living
 * on the card.
 *
 * The card is clickable as a whole *and* holds a separately focusable trigger, so
 * the click target is a stretched overlay button rather than a wrapper with
 * `role="button"` — nesting one control inside another breaks both semantics and
 * keyboard order.
 */
export default function TemplateOptionCard({ name, meta = [], sections, image, onSelect }: Props) {
    const metaParts = meta.filter(Boolean) as string[];
    const hasSections = Boolean(sections?.length);

    return (
        <div className="group relative rounded-xl border border-border/80 bg-card/95 p-4 shadow-elevation-1 transition-[border-color,box-shadow,transform] duration-200 ease-out focus-within:border-brand/20 hover:-translate-y-0.5 hover:border-brand/20 hover:shadow-elevation-2">
            {/* An invisible hit area, not a styled control: everything except the
                focus ring is neutralized, and the visible surface is the card. */}
            <Button
                variant="ghost"
                onClick={onSelect}
                aria-label={`Usar ${name}`}
                className="absolute inset-0 size-auto rounded-xl p-0 hover:bg-transparent"
            />

            {/* Above the overlay so text stays readable, but click-through so the
                whole card keeps selecting; only the count opts back in. */}
            <div className="pointer-events-none relative z-10">
                {image !== undefined && (
                    <div className="mb-3 flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-muted/40">
                        {image && (
                            <img
                                src={image}
                                alt=""
                                width={476}
                                height={387}
                                decoding="async"
                                className="size-full object-contain p-2"
                            />
                        )}
                    </div>
                )}

                <p className="text-sm font-medium text-foreground">{name}</p>

                {(metaParts.length > 0 || hasSections) && (
                    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                        {metaParts.map((part, index) => (
                            <span key={part} className="flex items-center gap-x-1.5">
                                {index > 0 && <span aria-hidden>·</span>}
                                {part}
                            </span>
                        ))}

                        {hasSections && (
                            <>
                                {metaParts.length > 0 && <span aria-hidden>·</span>}
                                <Popover>
                                    <PopoverTrigger
                                        className={cn(
                                            'pointer-events-auto relative z-20 rounded-sm underline decoration-dotted underline-offset-4',
                                            'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        )}
                                    >
                                        {sections!.length} seções
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="start"
                                        side="bottom"
                                        className="w-64 p-3"
                                        onOpenAutoFocus={(event) => event.preventDefault()}
                                    >
                                        {/* Just "Seções": the card name reads badly in a
                                            possessive ("Seções de com modelo") and only one
                                            panel is ever open. */}
                                        <p className="text-xs font-medium text-foreground">
                                            Seções
                                        </p>
                                        <ul className="mt-2 space-y-1">
                                            {sections!.map((section) => (
                                                <li
                                                    key={section}
                                                    className="text-xs text-muted-foreground"
                                                >
                                                    {section}
                                                </li>
                                            ))}
                                        </ul>
                                    </PopoverContent>
                                </Popover>
                            </>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}
